import { useState } from "react";
import { PanelMenu } from "primereact/panelmenu";
import { Button } from "primereact/button";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuthorization } from "../hooks/useAuthPermissions";
import { AppPermissions } from "../constants/permissions"; // Import the constants

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState({
    testCaseManagement: true,
    testExecutor: true,
    testLab: true,
  });

  const { can } = useAuthorization();
  const navigate = useNavigate();
  const { projectId } = useParams();
  const location = useLocation();

  const toggleSidebar = () => {
    setCollapsed(!collapsed);
    if (!collapsed) {
      setExpandedKeys({});
    }
  };

  const isActive = (item) => {
    const searchParams = new URLSearchParams(location.search);
    const activeTab = searchParams.get("activeTab");

    if (item.key === "jobs") return activeTab === "0";
    if (item.key === "devices") return activeTab === "1";
    if (item.key === "trays") return activeTab === "2";
    if (item.key === "files") return activeTab === "3";

    if (item.path) return location.pathname === item.path;
    if (item.items) return item.items.some((sub) => isActive(sub));
    return false;
  };

  const handleIconClick = (item) => {
    if (item.items) {
      setCollapsed(false);
      setExpandedKeys({ [item.key]: true });
    } else if (item.command) {
      item.command();
    }
  };

  const testCaseManagementItems = [
    {
      label: "Test Case Manager",
      icon: "pi pi-pen-to-square",
      path: `/${projectId}/test-case-manager`,
      command: () => navigate(`/${projectId}/test-case-manager`),
      visible: can(AppPermissions.TESTCASES_READ), // Check permission here
    },
    {
      label: "Test Data",
      icon: "pi pi-database",
      path: `/${projectId}/test-data-design`,
      command: () => navigate(`/${projectId}/test-data-design`),
      visible: can(AppPermissions.TESTDATA_CREATE),
    },
    {
      label: "Edit in Files",
      icon: "pi pi-file-edit",
      path: `/${projectId}/edit-files`,
      command: () => navigate(`/${projectId}/edit-files`),
      visible: can(AppPermissions.PROJECT_UPDATE),
    },
    {
      label: "Change Requests",
      icon: "pi pi-share-alt",
      path: `/${projectId}/change-requests`,
      command: () => navigate(`/${projectId}/change-requests`),
      visible: can(AppPermissions.CHANGE_REQUEST_LIST),
    },
  ].filter((item) => item.visible !== false); // Filter out hidden items

  const items = [
    {
      key: "allProjects",
      label: "All Projects",
      icon: "pi pi-folder",
      path: "/",
      command: () => navigate("/"),
      visible: can(AppPermissions.PROJECT_LIST),
    },
    {
      key: "testCaseManagement",
      label: "Test Case Management",
      icon: "pi pi-receipt",
      items: testCaseManagementItems,
      // Only show the parent if it has visible children
      visible: testCaseManagementItems.length > 0,
    },
    {
      key: "testExecutor",
      label: "Test Executor",
      icon: "pi pi-send",
      items: [
        {
          label: "Build Suite",
          icon: "pi pi-hammer",
          path: `/${projectId}/suite-build/v1`,
          command: () => navigate(`/${projectId}/suite-build/v1`),
          visible: can(AppPermissions.SUITE_CREATE),
        },
        {
          label: "Execute Tests",
          icon: "pi pi-play",
          path: `/${projectId}/suite-tree/v1`,
          command: () => navigate(`/${projectId}/suite-tree/v1`),
          visible: true, // Assuming general access
        },
        {
          label: "Job Templates",
          icon: "pi pi-list-check",
          path: `/${projectId}/job-templates`,
          command: () => navigate(`/${projectId}/job-templates`),
          visible: can(AppPermissions.JOBTEMPLATE_SELECT),
        },
      ].filter((item) => item.visible !== false),
    },
    {
      key: "testLab",
      label: "Test Lab",
      icon: "pi pi-pen-to-square",
      items: [
        {
          label: "Element Extractor",
          icon: "pi pi-search",
          path: `/${projectId}/element-extractor`,
          command: () => navigate(`/${projectId}/element-extractor`),
          visible: can(AppPermissions.TESTDATA_CREATE),
        },
      ],
      visible: can(AppPermissions.TESTDATA_CREATE),
    },
    {
      key: "jobs",
      label: "Jobs",
      icon: "pi pi-clock",
      path: `/${projectId}`,
      command: () => navigate(`/${projectId}?activeTab=0`),
      visible: can(AppPermissions.JOBS_LIST),
    },
    {
      key: "devices",
      label: "Devices",
      icon: "pi pi-mobile",
      path: `/${projectId}`,
      command: () => navigate(`/${projectId}?activeTab=1`),
      visible: can(AppPermissions.DEVICES_LIST),
    },
    {
      key: "trays",
      label: "Trays",
      icon: "pi pi-list",
      path: `/${projectId}`,
      command: () => navigate(`/${projectId}?activeTab=2`),
      visible: can(AppPermissions.TRAYS_READ),
    },
    {
      key: "files",
      label: "Files",
      icon: "pi pi-file",
      path: `/${projectId}`,
      command: () => navigate(`/${projectId}?activeTab=3`),
      visible: true,
    },
  ].filter((item) => item.visible !== false); // Final filter for top-level items

  const decorateItems = (items) => {
    return items.map((item) => ({
      ...item,
      ...(item.items ? { items: decorateItems(item.items) } : {}),
      ...(isActive(item) &&
      (!item.items || ["jobs", "devices", "trays", "files"].includes(item.key))
        ? {
            style: {
              background: "#ddd",
              borderRight: "4px solid #ef4e90",
            },
          }
        : {}),
    }));
  };

  return (
    <div style={{ position: "relative" }}>
      <div
        style={{
          height: "calc(100vh - 60px)",
          width: collapsed ? "60px" : "240px",
          background: "#f5f5f5",
          borderRight: "1px solid #ddd",
          transition: "width 0.3s",
          overflowY: "auto",
          overflowX: "hidden",
          paddingTop: "50px",
        }}
      >
        {!collapsed && (
          <PanelMenu
            model={decorateItems(items)}
            expandedKeys={expandedKeys}
            onExpandedKeysChange={setExpandedKeys}
            style={{ width: "100%" }}
            multiple
          />
        )}

        {collapsed && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              marginTop: "20px",
              gap: "10px",
            }}
          >
            {items.map((item) => (
              <div
                key={item.key}
                style={{
                  cursor: "pointer",
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  padding: "10px",
                  borderRight: isActive(item) ? "4px solid #ef4e90" : "none",
                  background: isActive(item) ? "#ddd" : "transparent",
                }}
                onClick={() => handleIconClick(item)}
              >
                <i
                  className={item.icon}
                  style={{ fontSize: "1.5rem" }}
                  title={item.label}
                ></i>
              </div>
            ))}
          </div>
        )}
      </div>

      <Button
        icon={`pi ${collapsed ? "pi-angle-right" : "pi-angle-left"}`}
        onClick={toggleSidebar}
        className="p-button-rounded p-button-sm"
        style={{
          height: "30px",
          width: "30px",
          position: "absolute",
          top: "25px",
          right: "-15px",
          transform: "translateY(-50%)",
          zIndex: 10,
        }}
      />
    </div>
  );
}
